import {
  InstallmentStatus,
  Order,
  OrderPaymentCard,
  OrderPaymentInstallment,
  OrderStatus,
} from '../../../../models/order.model';

export function getDownPayment(
  order: Order | null,
): OrderPaymentInstallment | null {
  const installments = order?.paymentPlan?.installments;
  if (!installments?.length) return null;

  return installments.find((x) => x.number == 0) ?? null;
}

export function getCancellationHistory(order: Order | null) {
  const cancelledStatus = OrderStatus.Cancelled;

  return (
    order?.histories
      ?.filter((h) => h.to === cancelledStatus)
      .sort(
        (a, b) =>
          new Date(b.transitionedAt).getTime() -
          new Date(a.transitionedAt).getTime(),
      )[0] ?? null
  );
}

export function isOrderCancelled(order: Order | null): boolean {
  return order?.status === OrderStatus.Cancelled;
}

export function canCancelOrder(order: Order | null): boolean {
  if (!order) return false;

  return ![OrderStatus.Rejected, OrderStatus.Cancelled].includes(order.status);
}

/**
 * Status used to decide how far the order progressed. For a cancelled order
 * it is the status held right before the cancellation, taken from the history.
 */
export function getEffectiveOrderStatus(order: Order): OrderStatus {
  if (order.status !== OrderStatus.Cancelled) return order.status;

  return getCancellationHistory(order)?.from ?? order.status;
}

export function getDownPaymentStatus(
  order: Order | null,
): InstallmentStatus | null {
  return getDownPayment(order)?.status ?? null;
}

/**
 * The API only refunds the down payment when the order is cancelled while
 * Reserved or ContractPending (OrderCancelledEventHandler). Past those stages
 * the paid amount is kept.
 */
export function isDownPaymentRefundable(order: Order | null): boolean {
  if (!order) return false;

  return (
    [OrderStatus.Reserved, OrderStatus.ContractPending].includes(
      getEffectiveOrderStatus(order),
    ) && getDownPaymentStatus(order) === InstallmentStatus.Paid
  );
}

export function getDefaultPaymentCard(
  order: Order | null,
): OrderPaymentCard | null {
  return order?.paymentCards?.find((x) => x.isDefault) ?? null;
}
